import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Centrifuge, Subscription } from 'centrifuge';

import { MirrorService } from 'services/MirrorService';

import { DialogsApi } from 'api/DialogsApi';
import { useUserId } from 'hooks/useUserId';
import { useWebSocketHandlers } from 'hooks/useWebSocketHandlers';
import { getAuthenticated } from 'store/auth/selectors';

const NAMESPACE = 'public';

enum WSChannel {
  Messages = 'messages',
  NewLikes = 'new_likes',
  Notifications = 'notifications',
  PaymentOrder = 'payment_order',
  UserInfo = 'user_info',
}

interface WebSocketSubscriptions {
  messageSubscription: Subscription | null;
  paymentOrderSubscription: Subscription | null;
  notificationSubscription: Subscription | null;
  newLikesSubscription: Subscription | null;
  userInfoSubscription: Subscription | null;
}

const getToken = async () => {
  try {
    const { token } = await DialogsApi.fetchWsToken();

    return token;
  } catch (error) {
    throw new Error(`Unexpected status code from getToken: ${error}`);
  }
};

export const WebSocketContext = createContext<WebSocketSubscriptions>({
  messageSubscription: null,
  paymentOrderSubscription: null,
  notificationSubscription: null,
  newLikesSubscription: null,
  userInfoSubscription: null,
});

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isConnected, setIsConnected] = useState(false);

  const isAuthenticated = useSelector(getAuthenticated);

  const { userId } = useUserId();

  const {
    messageHandler,
    notificationsHandler,
    newLikesHandler,
    paymentOrderHandler,
    userInfoHandler,
  } = useWebSocketHandlers();

  const centrifuge = useMemo(() => {
    if (!isAuthenticated || !userId) return null;

    return new Centrifuge(`${MirrorService.wsUrl}/connection/websocket`, {
      getToken,
    });
  }, [isAuthenticated, userId]);

  const {
    messageSubscription,
    paymentOrderSubscription,
    notificationSubscription,
    newLikesSubscription,
    userInfoSubscription,
  } = useMemo<WebSocketSubscriptions>(() => {
    if (!centrifuge || !userId)
      return {
        messageSubscription: null,
        paymentOrderSubscription: null,
        notificationSubscription: null,
        newLikesSubscription: null,
        userInfoSubscription: null,
      };

    return {
      messageSubscription: centrifuge.newSubscription(
        `${NAMESPACE}:${WSChannel.Messages}#${userId}`
      ),
      paymentOrderSubscription: centrifuge.newSubscription(
        `${NAMESPACE}:${WSChannel.PaymentOrder}#${userId}`
      ),
      notificationSubscription: centrifuge.newSubscription(
        `${NAMESPACE}:${WSChannel.Notifications}#${userId}`
      ),
      newLikesSubscription: centrifuge.newSubscription(
        `${NAMESPACE}:${WSChannel.NewLikes}#${userId}`
      ),
      userInfoSubscription: centrifuge.newSubscription(
        `${NAMESPACE}:${WSChannel.UserInfo}#${userId}`
      ),
    };
  }, [centrifuge, userId]);

  const contextValue = useMemo(
    () => ({
      messageSubscription,
      paymentOrderSubscription,
      notificationSubscription,
      newLikesSubscription,
      userInfoSubscription,
    }),
    [
      messageSubscription,
      paymentOrderSubscription,
      notificationSubscription,
      newLikesSubscription,
      userInfoSubscription,
    ]
  );

  const clearCentrifugeSubscriptions = useCallback(() => {
    messageSubscription?.unsubscribe();
    paymentOrderSubscription?.unsubscribe();
    notificationSubscription?.unsubscribe();
    newLikesSubscription?.unsubscribe();
    userInfoSubscription?.unsubscribe();

    centrifuge?.removeSubscription(messageSubscription);
    centrifuge?.removeSubscription(paymentOrderSubscription);
    centrifuge?.removeSubscription(notificationSubscription);
    centrifuge?.removeSubscription(newLikesSubscription);
    centrifuge?.removeSubscription(userInfoSubscription);

    centrifuge?.removeAllListeners();
    centrifuge?.disconnect();

    setIsConnected(false);
  }, [
    messageSubscription,
    paymentOrderSubscription,
    notificationSubscription,
    newLikesSubscription,
    userInfoSubscription,
    centrifuge,
  ]);

  useEffect(() => {
    if (centrifuge && isAuthenticated && !isConnected) {
      try {
        centrifuge.connect();

        setIsConnected(true);
      } catch (error) {
        console.error('Failed to connect to Centrifuge:', error);
      }
    }
  }, [centrifuge, isAuthenticated, isConnected]);

  useEffect(() => {
    messageSubscription?.subscribe();
    paymentOrderSubscription?.subscribe();
    notificationSubscription?.subscribe();
    newLikesSubscription?.subscribe();
    userInfoSubscription?.subscribe();

    return () => {
      clearCentrifugeSubscriptions();
    };
  }, [
    messageSubscription,
    notificationSubscription,
    paymentOrderSubscription,
    newLikesSubscription,
    userInfoSubscription,
    clearCentrifugeSubscriptions,
  ]);

  useEffect(() => {
    if (isAuthenticated === false) {
      clearCentrifugeSubscriptions();
    }
  }, [clearCentrifugeSubscriptions, isAuthenticated]);

  useEffect(() => {
    paymentOrderSubscription?.on('publication', paymentOrderHandler);

    return () => {
      paymentOrderSubscription?.removeListener(
        'publication',
        paymentOrderHandler
      );
    };
  }, [paymentOrderHandler, paymentOrderSubscription]);

  useEffect(() => {
    messageSubscription?.on('publication', messageHandler);

    return () => {
      messageSubscription?.removeListener('publication', messageHandler);
    };
  }, [messageHandler, messageSubscription]);

  useEffect(() => {
    notificationSubscription?.on('publication', notificationsHandler);

    return () => {
      notificationSubscription?.removeListener(
        'publication',
        notificationsHandler
      );
    };
  }, [notificationsHandler, notificationSubscription]);

  useEffect(() => {
    newLikesSubscription?.on('publication', newLikesHandler);

    return () => {
      newLikesSubscription?.removeListener('publication', newLikesHandler);
    };
  }, [newLikesHandler, newLikesSubscription]);

  useEffect(() => {
    userInfoSubscription?.on('publication', userInfoHandler);

    return () => {
      userInfoSubscription?.removeListener('publication', userInfoHandler);
    };
  }, [userInfoHandler, userInfoSubscription]);

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};
